import { useMemo } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { withTranslation } from "react-i18next";
import { Badge, Card, CardBody } from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import { MOCK_UPCOMING_ASSIGNMENTS } from "../mockUpcomingAssignments";

function UpcomingAssignmentsTable({ t }) {
  const columns = useMemo(
    () => [
      {
        header: t("workerDashboardColBookingId", "Booking ID"),
        accessorKey: "booking.slug",
        enableColumnFilter: false,
        cell: (info) => {
          const request = info.row.original;
          return (
            <Link
              to={`/my-assignment/${request.slug}`}
              className="text-primary fw-medium"
            >
              {info.getValue() || "-"}
            </Link>
          );
        },
      },
      {
        header: t("workerDashboardColRole", "Role"),
        accessorKey: "role.title",
        enableColumnFilter: false,
        cell: (info) => info.row.original.role?.title || "-",
      },
      {
        header: t("workerDashboardColLocation", "Location"),
        accessorKey: "booking.location.siteName",
        enableColumnFilter: false,
        cell: (info) =>
          info.row.original.booking?.location?.siteName || "-",
      },
      {
        header: t("workerDashboardColDates", "Dates"),
        accessorKey: "dates",
        enableColumnFilter: false,
        cell: (info) => {
          const dates = info.getValue() || [];
          if (dates.length === 0) return "-";
          return (
            <div className="d-flex flex-wrap gap-1">
              {dates.slice(0, 3).map((date, idx) => (
                <Badge key={idx} className="bg-primary">
                  {dayjs(date).format("DD MMM")}
                </Badge>
              ))}
              {dates.length > 3 && (
                <Badge className="bg-secondary">
                  +{dates.length - 3}{" "}
                  {t("workerDashboardMoreDates", "more")}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        header: t("workerDashboardColShiftTime", "Shift time"),
        accessorKey: "shift.startTime",
        enableColumnFilter: false,
        cell: (info) => {
          const shift = info.row.original.shift;
          if (!shift) return "-";
          return `${shift.startTime || "-"} - ${shift.endTime || "-"}`;
        },
      },
      {
        header: t("workerDashboardColExpiresAt", "Expires at"),
        accessorKey: "expiresAt",
        enableColumnFilter: false,
        cell: (info) => {
          const expiresAt = info.getValue();
          if (!expiresAt) return "-";
          const isExpired = dayjs(expiresAt).isBefore(dayjs());
          return (
            <div>
              <div>{dayjs(expiresAt).format("DD MMM YYYY, HH:mm")}</div>
              {isExpired && (
                <Badge className="bg-danger mt-1">
                  {t("workerDashboardExpired", "Expired")}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        header: t("workerDashboardColClient", "Client"),
        accessorKey: "booking.createdBy.fullName",
        enableColumnFilter: false,
        cell: (info) =>
          info.row.original.booking?.createdBy?.fullName || "-",
      },
      {
        header: t("workerDashboardColCreatedDate", "Created date"),
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) =>
          info.getValue()
            ? dayjs(info.getValue()).format("DD MMM YYYY")
            : "-",
      },
      {
        header: t("workerDashboardColAction", "Action"),
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const request = cellProps.row.original;
          return (
            <Link
              to={`/my-assignment/${request.slug}`}
              className="btn btn-sm btn-soft-info"
            >
              {t("workerDashboardView", "View")}{" "}
              <i className="mdi mdi-eye-outline" />
            </Link>
          );
        },
      },
    ],
    [t]
  );

  return (
    <Card className="worker-dashboard-section border-0 shadow-sm mb-4">
      <CardBody className="p-4">
        <h5 className="worker-dashboard-section__title mb-4">
          {t("workerDashboardUpcomingAssignments", "Upcoming assignments")}
        </h5>
        <TableContainer
          columns={columns}
          data={MOCK_UPCOMING_ASSIGNMENTS}
          isPagination={false}
          isCustomPageSize={false}
          isGlobalFilter={false}
          isJobListGlobalFilter={false}
          tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline border-top"
          theadClass="table-light"
        />
      </CardBody>
    </Card>
  );
}

UpcomingAssignmentsTable.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(UpcomingAssignmentsTable);
